import { useState } from 'react';
import { Link } from 'react-router-dom';

import { buildIntegrationPrompt } from '@admin-platform/shared/integration-content';
import { endpointsForPage } from '../api-endpoints';
import { AnchorHeading } from '../components/AnchorHeading';
import { OnThisPage } from '../components/OnThisPage';
import { useDocsPage } from '../hooks/useDocsPage';

const QUERY = `query Client($ref: String!) {
  client(externalId: $ref) {
    id
    externalId
    name
    region { id name defaultCurrency }
    subscriptions {
      id
      status
      period
      currency
      autoRenew
      billingArrangement
      planChargeBilledThrough
      awaitingActivation
    }
    invoices(status: "OVERDUE", limit: 10) {
      invoiceNumber
      paymentStatus
      dueDate
      amount { amount currency }
      pdfUrl
    }
    paymentSummary { currency outstanding overdue overdueCount }
  }
}`;

const CURL = `curl -s -X POST https://api.revvly.ai/api/v1/graphql \\
  -H "Authorization: Bearer $REVVLY_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "query": "query($ref: String!) { client(externalId: $ref) { id name region { id defaultCurrency } subscriptions { id status } invoices { invoiceNumber paymentStatus amount { amount currency } } paymentSummary { currency outstanding overdue } } }",
    "variables": { "ref": "acct_123" }
  }'`;

const NODE = `const res = await fetch('https://api.revvly.ai/api/v1/graphql', {
  method: 'POST',
  headers: {
    'Authorization': \`Bearer \${process.env.REVVLY_TOKEN}\`,
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    query: \`${QUERY}\`,
    variables: { ref: 'acct_123' },
  }),
});
const { data } = await res.json();
console.log(data.client);`;

const PYTHON = `import os, json, urllib.request

body = json.dumps({
    "query": """${QUERY}""",
    "variables": {"ref": "acct_123"},
}).encode()

req = urllib.request.Request(
    "https://api.revvly.ai/api/v1/graphql",
    data=body,
    headers={
        "Authorization": f"Bearer {os.environ['REVVLY_TOKEN']}",
        "Content-Type": "application/json",
    },
)
with urllib.request.urlopen(req) as res:
    print(json.load(res)["data"]["client"])`;

const CREATE_MUTATION = `mutation CreateClient($input: CreateClientInput!) {
  createClient(input: $input) {
    id
    externalId
    name
    region { id name }
  }
}

# variables
{
  "input": {
    "name": "Acme Corporation",
    "regionId": "663b1f2e4a3c2d1e0f8b7a55",
    "externalId": "acct_123",
    "billingAddress": "1 Market St, San Francisco, CA",
    "taxId": "US-123456789",
    "paymentTermsDays": 30,
    "renewalInvoiceLeadDays": { "monthly": 7, "yearly": 30 }
  }
}`;

const CREATE_RESPONSE = `{
  "data": {
    "createClient": {
      "id":         "663b1f2e4a3c2d1e0f8b7a01",
      "externalId": "acct_123",
      "name":       "Acme Corporation",
      "region":     { "id": "663b1f2e4a3c2d1e0f8b7a55", "name": "United States" }
    }
  }
}`;

const CREATE_SUBSCRIPTION_MUTATION = `mutation CreateSubscription($input: CreateSubscriptionInput!) {
  createSubscription(input: $input) {
    id
    externalId
    status
    period
    currency
    startDate
    endDate
    autoRenew
  }
}

# variables
{
  "input": {
    "clientExternalId": "h_4471",
    "externalId": "h_4471",
    "bundleId": "663b1f2e4a3c2d1e0f8b7a9e",
    "period": "MONTHLY",
    "startDate": "2026-09-24T00:00:00.000Z",
    "components": [
      { "componentId": "663b1f2e4a3c2d1e0f8b7b10", "startingQuantity": 2 }
    ]
  }
}`;

const CREATE_SUBSCRIPTION_RESPONSE = `{
  "data": {
    "createSubscription": {
      "id":         "663b1f2e4a3c2d1e0f8b7c01",
      "externalId": "h_4471",
      "status":     "ACTIVE",
      "period":     "MONTHLY",
      "currency":   "USD",
      "startDate":  "2026-09-24T00:00:00.000Z",
      "endDate":    "2026-10-24T00:00:00.000Z",
      "autoRenew":  true
    }
  }
}`;

const CREATE_PROSPECT_MUTATION = `mutation CreateProspect($input: CreateProspectInput!) {
  createProspect(input: $input) {
    organization { id name domain email }
    contact { id firstName lastName email phone }
    lead { id title externalRef pipelineId pipelineStageId }
  }
}

# variables
{
  "input": {
    "organization": {
      "name": "Northstar Labs",
      "domain": "northstarlabs.example",
      "industry": "Software"
    },
    "contact": {
      "firstName": "Avery",
      "lastName": "Chen",
      "email": "avery@northstarlabs.example",
      "phone": "+1 415 555 0142"
    },
    "lead": {
      "title": "Enterprise demo request",
      "externalRef": "hubspot-form-evt_01J8K3",
      "estimatedValue": 24000,
      "currency": "USD"
    }
  }
}`;

const CREATE_PROSPECT_RESPONSE = `{
  "data": {
    "createProspect": {
      "organization": {
        "id": "663b1f2e4a3c2d1e0f8b7b01",
        "name": "Northstar Labs",
        "domain": "northstarlabs.example",
        "email": null
      },
      "contact": {
        "id": "663b1f2e4a3c2d1e0f8b7b02",
        "firstName": "Avery",
        "lastName": "Chen",
        "email": "avery@northstarlabs.example",
        "phone": "+1 415 555 0142"
      },
      "lead": {
        "id": "663b1f2e4a3c2d1e0f8b7b03",
        "title": "Enterprise demo request",
        "externalRef": "hubspot-form-evt_01J8K3",
        "pipelineId": "663b1f2e4a3c2d1e0f8b7b04",
        "pipelineStageId": "663b1f2e4a3c2d1e0f8b7b05"
      }
    }
  }
}`;

const AGENT_PROMPT = `${buildIntegrationPrompt('clientApi')}

${buildIntegrationPrompt('prospectForm')}`;

const RESPONSE = `{
  "data": {
    "client": {
      "id":         "663b1f2e4a3c2d1e0f8b7a01",
      "externalId": "acct_123",
      "name":       "Acme Corporation",
      "region": {
        "id":              "663b1f2e4a3c2d1e0f8b7a55",
        "name":            "United States",
        "defaultCurrency": "USD"
      },
      "subscriptions": [
        {
          "id":                      "663b…c1",
          "status":                  "ACTIVE",
          "period":                  "MONTHLY",
          "currency":                "USD",
          "autoRenew":               true,
          "billingArrangement":      "PREPAID_TERM",
          "planChargeBilledThrough": "2026-09-01T00:00:00.000Z",
          "awaitingActivation":      false
        }
      ],
      "invoices": [
        {
          "invoiceNumber": "ACME-00007",
          "paymentStatus": "OVERDUE",
          "dueDate":       "2026-06-04T00:00:00.000Z",
          "amount":        { "amount": 1200.00, "currency": "USD" },
          "pdfUrl":        "https://files.revvly.com/…/ACME-00007.pdf?X-Amz-Expires=1800&…"
        }
      ],
      "paymentSummary": [
        { "currency": "USD", "outstanding": 1200.00, "overdue": 1200.00, "overdueCount": 1 }
      ]
    }
  }
}`;

const ERROR = `{
  "errors": [
    {
      "message": "No client found for externalId \\"ghost\\"",
      "code": "NOT_FOUND",
      "path": ["client"],
      "extensions": { "code": "NOT_FOUND" }
    }
  ],
  "data": { "client": null }
}`;

type Lang = 'curl' | 'node' | 'python';

const BY_LANG: Record<Lang, string> = { curl: CURL, node: NODE, python: PYTHON };

export function PublicGraphQLApi() {
  const [lang, setLang] = useState<Lang>('curl');
  useDocsPage('Public GraphQL API — Revvly Docs');

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/api">API</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Public GraphQL</span>
      </nav>
      <header className="dp-header">
        <span className="rv-eyebrow-mini">API Reference</span>
        <h1>
          Read data, provision clients and subscriptions, and submit prospects with the{' '}
          <em>public GraphQL API</em>.
        </h1>
        <p className="dp-oneliner">
          One endpoint, one round-trip: fetch a client together with its region, subscriptions,
          invoices, and payment status; create a client and a subscription for it, each with your
          own externalId; or send a complete prospect form submission. Authenticated with the same
          API keys as the rest of the platform.
        </p>
      </header>

      <OnThisPage endpoints={endpointsForPage('/docs/api/graphql-public')} />

      {/* ── Why GraphQL here ───────────────────────────────────── */}
      <section className="dp-section">
        <h2>REST for ingestion, GraphQL for the rest</h2>
        <p className="dp-section-sub">
          Revvly&rsquo;s external API is split by shape. High-volume ingestion &mdash; usage and
          event <Link to="/docs/api/events">streams</Link> &mdash; stays on REST, where flat
          payloads and HMAC signing fit. Everything else lives here on GraphQL: reads of related
          data &mdash; a client together with its subscriptions, invoices, and payment status
          &mdash; where you select the nested fields you want in one request, and client management
          &mdash; creating a client, provisioning a subscription for it, and submitting a prospect
          form &mdash; through mutations.
        </p>
        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            This is a <strong>dedicated, curated public schema</strong> &mdash; separate from
            Revvly&rsquo;s internal dashboard API. It exposes only the types documented here, so it
            is safe to build against as a stable contract.
          </span>
        </div>
      </section>

      {/* ── Endpoint & auth ────────────────────────────────────── */}
      <section className="dp-section">
        <AnchorHeading id="graphql">Endpoint &amp; authentication</AnchorHeading>
        <div className="api-endpoint-bar">
          <span className="api-method api-method-post">POST</span>
          <span className="api-url">https://api.revvly.ai/api/v1/graphql</span>
          <span className="api-returns">application/json</span>
        </div>
        <p className="dp-section-sub">
          Send a standard GraphQL request (a <span className="api-inline">query</span> plus optional{' '}
          <span className="api-inline">variables</span>) as JSON. Authenticate with a{' '}
          <span className="api-inline">rvly_sk_live_*</span> API key in the{' '}
          <span className="api-inline">Authorization</span> header &mdash; the same keys used for
          the REST surface. Every request is scoped to the key&rsquo;s tenant; you can never read
          another tenant&rsquo;s data.
        </p>

        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Authorization</div>
            <div className="dp-field-v">
              <span className="api-inline">Bearer rvly_sk_live_&lt;your-token&gt;</span>. Missing or
              invalid ⇒ <span className="api-inline">UNAUTHENTICATED</span>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Capabilities</div>
            <div className="dp-field-v">
              Every key holds <span className="api-inline">READ</span>, which covers queries.
              Mutations need <span className="api-inline">WRITE</span>, except{' '}
              <span className="api-inline">createEndUserToken</span> and{' '}
              <span className="api-inline">upsertSupportUsers</span> (
              <span className="api-inline">SUPPORT_TOKEN_MINT</span>),{' '}
              <span className="api-inline">submitForm</span> (
              <span className="api-inline">FORM_SUBMIT</span>) and the knowledge mutations (
              <span className="api-inline">KNOWLEDGE_WRITE</span>), where{' '}
              <span className="api-inline">WRITE</span> is neither needed nor sufficient. A key
              without the needed capability ⇒ <span className="api-inline">FORBIDDEN</span>. See the{' '}
              <Link to="/docs/reference/api-keys">API key reference</Link>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Rate limit</div>
            <div className="dp-field-v">
              Per-key, shared with the REST surface: 10 per second and 100 per minute unless the
              issuer sets other values. Over the limit ⇒{' '}
              <span className="api-inline">RATE_LIMITED</span> with a{' '}
              <span className="api-inline">Retry-After</span> header.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Query depth</div>
            <div className="dp-field-v">
              Nesting is bounded; excessively deep queries are rejected with{' '}
              <span className="api-inline">QUERY_DEPTH_EXCEEDED</span> before execution.
            </div>
          </div>
        </div>

        <span className="api-code-label">Request headers</span>
        <pre className="api-code">{`Authorization: Bearer rvly_sk_live_<your-token>
Content-Type:  application/json`}</pre>
      </section>

      <section className="dp-section">
        <h2>Give this prompt to an integration agent</h2>
        <p className="dp-plain">
          Copy this context when an agent needs to build a client read, provisioning, or prospect
          submission integration.
        </p>
        <pre className="api-code">{AGENT_PROMPT}</pre>
      </section>

      {/* ── The client query ───────────────────────────────────── */}
      <section className="dp-section">
        <AnchorHeading id="client">Fetch a client</AnchorHeading>
        <p className="dp-section-sub">
          Look a client up by its Revvly <span className="api-inline">id</span> or by your own{' '}
          <span className="api-inline">externalId</span> &mdash; supply exactly one. Select only the
          fields you need.
        </p>

        <div className="api-tabs">
          {(['curl', 'node', 'python'] as Lang[]).map((l) => (
            <button
              key={l}
              className={`api-tab ${lang === l ? 'api-tab-active' : ''}`}
              onClick={() => setLang(l)}
            >
              {l === 'node' ? 'Node.js' : l === 'python' ? 'Python' : 'cURL'}
            </button>
          ))}
        </div>
        <pre className="api-code api-code-tabbed">{BY_LANG[lang]}</pre>

        <h3 className="api-sub-heading">Arguments</h3>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">id</span>
              <span className="api-badge api-badge-cond">one of</span>
            </div>
            <div className="dp-field-v">The internal Revvly client id.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">externalId</span>
              <span className="api-badge api-badge-cond">one of</span>
            </div>
            <div className="dp-field-v">
              Your own reference, set on the client (unique per tenant). Supplying both{' '}
              <span className="api-inline">id</span> and{' '}
              <span className="api-inline">externalId</span>, or neither, is a{' '}
              <span className="api-inline">BAD_REQUEST</span>.
            </div>
          </div>
        </div>

        <span className="api-code-label">Response</span>
        <pre className="api-code">{RESPONSE}</pre>
      </section>

      {/* ── Fields on a client ─────────────────────────────────── */}
      <section className="dp-section">
        <h2>What you can read on a client</h2>
        <p className="dp-section-sub">
          Beyond the client&rsquo;s own fields, four related selections let you pull a
          customer&rsquo;s whole billing picture in one request instead of stitching several REST
          calls together.
        </p>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">region</span>
              <span className="api-badge api-badge-cond">Region</span>
            </div>
            <div className="dp-field-v">
              The client&rsquo;s billing region resolved to a full object &mdash;{' '}
              <span className="api-inline">name</span>, <span className="api-inline">code</span>,{' '}
              <span className="api-inline">defaultCurrency</span>,{' '}
              <span className="api-inline">supportedCurrencies</span>. The bare{' '}
              <span className="api-inline">regionId</span> is still there if you only need the id.
              Null if the region was removed.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">subscriptions</span>
              <span className="api-badge api-badge-cond">[Subscription!]!</span>
            </div>
            <div className="dp-field-v">
              The client&rsquo;s subscriptions, newest first &mdash;{' '}
              <span className="api-inline">status</span>, <span className="api-inline">period</span>
              , <span className="api-inline">currency</span>,{' '}
              <span className="api-inline">startDate</span>/
              <span className="api-inline">endDate</span>,{' '}
              <span className="api-inline">autoRenew</span>, and your own{' '}
              <span className="api-inline">externalId</span>. Three further fields describe how the
              subscription is <em>invoiced</em>, separately from what it costs &mdash;{' '}
              <span className="api-inline">billingArrangement</span>,{' '}
              <span className="api-inline">planChargeBilledThrough</span> and{' '}
              <span className="api-inline">awaitingActivation</span>. Read them before reconciling
              invoices; see below.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">invoices</span>
              <span className="api-badge api-badge-cond">[Invoice!]!</span>
            </div>
            <div className="dp-field-v">
              The client&rsquo;s invoices, newest first, each with a{' '}
              <span className="api-inline">Money</span> <span className="api-inline">amount</span>,{' '}
              <span className="api-inline">paymentStatus</span> (
              <span className="api-inline">PAID</span> / <span className="api-inline">PENDING</span>{' '}
              / <span className="api-inline">OVERDUE</span> /{' '}
              <span className="api-inline">FAILED</span> /{' '}
              <span className="api-inline">CANCELLED</span>), and{' '}
              <span className="api-inline">dueDate</span> /{' '}
              <span className="api-inline">paidDate</span>. When the invoice is taxed,{' '}
              <span className="api-inline">amount</span> includes tax;{' '}
              <span className="api-inline">subtotal</span> is the net,{' '}
              <span className="api-inline">tax</span> the total tax and{' '}
              <span className="api-inline">taxBreakdown</span> the tax by code and rate (all null or
              empty when untaxed). A zero-
              <span className="api-inline">amount</span> invoice that is awaiting collection is
              settled the moment it is issued: it reads <span className="api-inline">PAID</span>{' '}
              with <span className="api-inline">paidDate</span> equal to its{' '}
              <span className="api-inline">invoiceDate</span>, and never counts as outstanding or
              overdue &mdash; there is nothing to collect on it. A zero-amount invoice you
              explicitly cancelled or marked failed keeps that status, so do not treat{' '}
              <span className="api-inline">amount</span> of zero as implying{' '}
              <span className="api-inline">PAID</span> &mdash; read{' '}
              <span className="api-inline">paymentStatus</span>. Each invoice also exposes{' '}
              <span className="api-inline">pdfUrl</span> &mdash; a{' '}
              <strong>short-lived (30-minute) presigned link</strong> to download the invoice PDF,
              minted fresh per request and <span className="api-inline">null</span> when no PDF has
              been generated yet. Request it only when you intend to download. Optional arguments:{' '}
              <span className="api-inline">status</span> (filter to one payment status) and{' '}
              <span className="api-inline">limit</span> (default 100, max 1000). This list stops at
              the newest <span className="api-inline">limit</span> invoices; to read a longer
              history, use <span className="api-inline">invoicesPage</span>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">invoicesPage</span>
              <span className="api-badge api-badge-cond">InvoicePage!</span>
            </div>
            <div className="dp-field-v">
              The client&rsquo;s whole invoice history, one page at a time, newest first. It returns{' '}
              <span className="api-inline">data</span> (the same{' '}
              <span className="api-inline">Invoice</span> objects as above) and{' '}
              <span className="api-inline">nextCursor</span>. Pass{' '}
              <span className="api-inline">nextCursor</span> back as{' '}
              <span className="api-inline">after</span> until it is{' '}
              <span className="api-inline">null</span>. A page never repeats or skips an invoice,
              even one issued while you are paging. Arguments:{' '}
              <span className="api-inline">status</span>, <span className="api-inline">limit</span>{' '}
              (default 100, max 1000 per page) and <span className="api-inline">after</span>. The
              cursor is opaque: one the API did not issue is a{' '}
              <span className="api-inline">BAD_REQUEST</span> (&ldquo;Invalid cursor&rdquo;), not an
              empty page. The status filter and the key&rsquo;s scope apply on every page.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">paymentSummary</span>
              <span className="api-badge api-badge-cond">[PaymentSummary!]!</span>
            </div>
            <div className="dp-field-v">
              Receivable rollups so you don&rsquo;t compute them yourself &mdash;{' '}
              <span className="api-inline">outstanding</span> (unpaid, non-cancelled) and{' '}
              <span className="api-inline">overdue</span> totals plus counts, returned{' '}
              <strong>one entry per currency</strong> so multi-currency clients aren&rsquo;t summed
              into a meaningless mixed total.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">customFields</span>
              <span className="api-badge api-badge-cond">[ClientCustomField!]!</span>
            </div>
            <div className="dp-field-v">
              The custom fields filled in on this client, read-only. Each entry has{' '}
              <span className="api-inline">key</span>, <span className="api-inline">label</span>,{' '}
              <span className="api-inline">fieldType</span> and{' '}
              <span className="api-inline">valueJson</span>, the value JSON-encoded (a string,
              number, boolean or list of strings; a select field holds option keys). Fields your
              workspace has limited to a role are not listed.
            </div>
          </div>
        </div>
      </section>

      {/* ── Billing arrangement ────────────────────────────────── */}
      <section className="dp-section">
        <AnchorHeading id="billing-arrangement">How a subscription is invoiced</AnchorHeading>
        <p className="dp-section-sub">
          A subscription&rsquo;s price and its invoicing schedule are two different things. Three
          read-only fields on <span className="api-inline">Subscription</span> describe the
          schedule, so that a renewal which legitimately raises no invoice doesn&rsquo;t read to
          your integration as a missing one.
        </p>
        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            <strong>Read these before reconciling invoices.</strong> Under{' '}
            <span className="api-inline">PREPAID_TERM</span>, several periods are billed on{' '}
            <em>one</em> invoice at signing — the plan and the components billed ahead with it — so
            a renewal inside that span raises no plan charge, invoices a component only for units
            added since signing, and raises no invoice at all when there is nothing to bill. An
            integration that treats each of those as a missing invoice double-counts the span.
          </span>
        </div>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">billingArrangement</span>
              <span className="api-badge api-badge-cond">String!</span>
            </div>
            <div className="dp-field-v">
              One of <span className="api-inline">STANDARD</span> (one plan charge per period, on
              renewal), <span className="api-inline">PREPAID_TERM</span> (several periods billed
              upfront on a single invoice, with the components billed ahead) or{' '}
              <span className="api-inline">DEFERRED_ACTIVATION</span> (part of the first period
              charged at signing, the rest when the customer goes live). Always present;{' '}
              <span className="api-inline">STANDARD</span> is the default and behaves exactly as it
              always has.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">planChargeBilledThrough</span>
              <span className="api-badge api-badge-cond">String</span>
            </div>
            <div className="dp-field-v">
              ISO 8601, or <span className="api-inline">null</span> when nothing is invoiced ahead.
              How far the <em>plan charge</em> has already been billed past the renewal date. Any
              renewal ending on or before this date raises no plan charge. Components billed ahead
              were prepaid with it, so a renewal invoices them only for units added since signing;
              usage counted per period still bills after each period, so a usage-only invoice inside
              the span is expected, not an anomaly — and so is a renewal with no invoice at all.
              Prefer checking this field over checking{' '}
              <span className="api-inline">billingArrangement</span>: an ordinary{' '}
              <span className="api-inline">STANDARD</span> subscription also sets it when the
              account has a renewal lead time that invoices ahead.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">awaitingActivation</span>
              <span className="api-badge api-badge-cond">Boolean!</span>
            </div>
            <div className="dp-field-v">
              True while the subscription is signed but not yet switched on. It earns no revenue,
              renews nothing, and rejects usage with a <span className="api-inline">409</span> until
              it is activated. Its <span className="api-inline">startDate</span> and{' '}
              <span className="api-inline">endDate</span> are <strong>provisional</strong> while
              this is true &mdash; activation re-anchors the term to the real go-live date &mdash;
              so don&rsquo;t treat them as the contract term or use them to schedule anything.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">trialEndsAt</span>
              <span className="api-badge api-badge-cond">String</span>
            </div>
            <div className="dp-field-v">
              ISO 8601. When the free trial ends and the first period is invoiced. While{' '}
              <span className="api-inline">status</span> is{' '}
              <span className="api-inline">TRIAL</span> nothing is billed and{' '}
              <span className="api-inline">startDate</span> /{' '}
              <span className="api-inline">endDate</span> describe the trial, not a paid term. When
              it converts, <span className="api-inline">startDate</span> moves to the day paid
              service began. Null when there was no trial.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">trialConvertedAt</span>
              <span className="api-badge api-badge-cond">String</span>
            </div>
            <div className="dp-field-v">
              ISO 8601. When the trial converted to paid. Null until it does.
            </div>
          </div>
        </div>
      </section>

      {/* ── Create a prospect ─────────────────────────────────── */}
      <section className="dp-section">
        <AnchorHeading id="create-prospect">Create a prospect from a form</AnchorHeading>
        <p className="dp-section-sub">
          Use <span className="api-inline">createProspect</span> for a CRM form submission, not{' '}
          <span className="api-inline">createClient</span>. One call creates a Prospect
          organization, its primary Contact, and a Lead in the tenant&rsquo;s default pipeline. It
          requires both a <span className="api-inline">WRITE</span>-capable key and CRM enabled for
          the tenant.
        </p>
        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            Use the form provider&rsquo;s immutable submission id as{' '}
            <span className="api-inline">lead.externalRef</span>. It is required and makes a
            completed submission safe to retry: Revvly returns the existing prospect instead of
            making another one.
          </span>
        </div>
        <pre className="api-code">{CREATE_PROSPECT_MUTATION}</pre>

        <h3 className="api-sub-heading">Input rules</h3>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">organization</span>
            </div>
            <div className="dp-field-v">
              A name is required; domain, email, description, industry, and regionId are optional. A
              supplied domain is normalized and must be unique per tenant.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">contact</span>
            </div>
            <div className="dp-field-v">
              Include at least one of first name, last name, or email. Contact email addresses are
              unique per tenant when supplied.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">lead</span>
            </div>
            <div className="dp-field-v">
              Title and <span className="api-inline">externalRef</span> are required. Pipeline and
              stage default to the tenant&rsquo;s default pipeline and first Open stage; estimated
              value and currency are optional.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">custom fields</span>
            </div>
            <div className="dp-field-v">
              Custom fields are not part of the public API yet. Do not send arbitrary form fields;
              only the documented inputs are stored.
            </div>
          </div>
        </div>
        <span className="api-code-label">Response</span>
        <pre className="api-code">{CREATE_PROSPECT_RESPONSE}</pre>
        <ul className="dp-connects">
          <li>
            <Link
              to="/docs/guides/prospect-form-integration"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Prospect form integration guide ›
            </Link>
          </li>
        </ul>
      </section>

      {/* ── Create a client ────────────────────────────────────── */}
      <section className="dp-section">
        <AnchorHeading id="create-client">Create a client</AnchorHeading>
        <p className="dp-section-sub">
          The <span className="api-inline">createClient</span> mutation creates a client in your
          tenant and returns it. Only <span className="api-inline">name</span> and{' '}
          <span className="api-inline">regionId</span> are required &mdash; every other field is
          optional. This is a write, so it needs an API key granted the{' '}
          <span className="api-inline">WRITE</span> capability; a{' '}
          <span className="api-inline">READ</span>-only key is rejected with{' '}
          <span className="api-inline">FORBIDDEN</span>.
        </p>

        <pre className="api-code">{CREATE_MUTATION}</pre>

        <h3 className="api-sub-heading">Input fields</h3>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">name</span>
              <span className="api-badge api-badge-req">required</span>
            </div>
            <div className="dp-field-v">
              Display name, 2&ndash;100 chars. Must be unique per tenant (case-insensitively)
              &mdash; a duplicate returns <span className="api-inline">CONFLICT</span>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">regionId</span>
              <span className="api-badge api-badge-req">required</span>
            </div>
            <div className="dp-field-v">
              The id of an <strong>active</strong> region (see the{' '}
              <Link to="/docs/api/external-references">regions discovery</Link> endpoint). An
              unknown region returns <span className="api-inline">NOT_FOUND</span>; an inactive one,{' '}
              <span className="api-inline">BAD_REQUEST</span>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">externalId</span>
            </div>
            <div className="dp-field-v">
              Your own reference for the client (unique per tenant, max 100). A duplicate returns{' '}
              <span className="api-inline">CONFLICT</span>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">incubatorId</span>
            </div>
            <div className="dp-field-v">Optional incubator / partner reference.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">invoicePrefix</span>
            </div>
            <div className="dp-field-v">
              Prefix for the client&rsquo;s invoice numbers, e.g.{' '}
              <span className="api-inline">INV-ACME</span> yields{' '}
              <span className="api-inline">INV-ACME-00042</span> (max 50). Unique per tenant; a
              duplicate returns <span className="api-inline">CONFLICT</span>. Omit it and one is
              derived from the client name.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">nextInvoiceNumber</span>
            </div>
            <div className="dp-field-v">
              The sequence number the client&rsquo;s next invoice should carry (min 1). Set it when
              the client already has invoice history elsewhere and the numbering has to continue — a
              client at invoice 14 takes <span className="api-inline">15</span>. Consumed by the
              first generated invoice; after that the sequence continues on its own.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">description</span>
            </div>
            <div className="dp-field-v">Free-text description (max 500).</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">billingAddress</span> /{' '}
              <span className="api-inline">taxId</span>
            </div>
            <div className="dp-field-v">Billing address (max 500) and tax / VAT id (max 50).</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">paymentTermsDays</span>
            </div>
            <div className="dp-field-v">
              Payment terms in days; overrides the tenant default when set.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">renewalInvoiceLeadDays</span>
            </div>
            <div className="dp-field-v">
              Per-period (<span className="api-inline">monthly</span> /{' '}
              <span className="api-inline">quarterly</span> /{' '}
              <span className="api-inline">yearly</span>) override of how many days before renewal
              to raise the invoice. Each period left unset inherits the tenant default; 0 disables
              it.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">isTrialActive</span>
            </div>
            <div className="dp-field-v">
              Whether the client starts on a trial (default{' '}
              <span className="api-inline">false</span>). When true, both{' '}
              <span className="api-inline">trialStartDate</span> and{' '}
              <span className="api-inline">trialEndDate</span> (ISO 8601) are required and start
              must precede end.
            </div>
          </div>
        </div>

        <span className="api-code-label">Response</span>
        <pre className="api-code">{CREATE_RESPONSE}</pre>
      </section>

      {/* ── Update a client ────────────────────────────────────── */}
      <section className="dp-section">
        <AnchorHeading id="update-client">Update a client</AnchorHeading>
        <p className="dp-section-sub">
          The <span className="api-inline">updateClient</span> mutation corrects a client and
          returns it. Name the client with exactly one of{' '}
          <span className="api-inline">clientId</span> or{' '}
          <span className="api-inline">clientExternalId</span>, then send only the fields you want
          to change: a field you leave out is left alone. It needs{' '}
          <span className="api-inline">WRITE</span>.
        </p>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">name</span>
            </div>
            <div className="dp-field-v">
              2&ndash;100 chars, unique in your tenant. Cannot be cleared.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">externalId</span> /{' '}
              <span className="api-inline">description</span> /{' '}
              <span className="api-inline">billingAddress</span> /{' '}
              <span className="api-inline">taxId</span>
            </div>
            <div className="dp-field-v">
              Same limits as on create. <span className="api-inline">null</span> clears the field. A
              new <span className="api-inline">externalId</span> already used by another client
              returns <span className="api-inline">CONFLICT</span>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">paymentTermsDays</span> /{' '}
              <span className="api-inline">renewalInvoiceLeadDays</span>
            </div>
            <div className="dp-field-v">
              Cannot be cleared. For <span className="api-inline">renewalInvoiceLeadDays</span>,
              only the periods you send change.
            </div>
          </div>
        </div>
        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            The region, the invoice prefix and numbering, trial dates and CRM fields are not part of
            the input, so they cannot be changed through the API. Sending{' '}
            <span className="api-inline">null</span> for a field that cannot be cleared, or a
            request that changes nothing, is a <span className="api-inline">BAD_REQUEST</span>. A
            scoped key reaches only clients that own a subscription in its scope; any other client
            reads as <span className="api-inline">NOT_FOUND</span>.
          </span>
        </div>
      </section>

      {/* ── Create a subscription ──────────────────────────────── */}
      <section className="dp-section">
        <AnchorHeading id="create-subscription">Create a subscription</AnchorHeading>
        <p className="dp-section-sub">
          The <span className="api-inline">createSubscription</span> mutation provisions a
          subscription for a client and returns it. It wraps the same pricing engine the
          dashboard&rsquo;s subscription wizard uses, behind a curated input &mdash; enough to
          onboard a subscription end-to-end from an integration, without the wizard&rsquo;s full
          authoring surface (see what&rsquo;s excluded, below). Like{' '}
          <span className="api-inline">createClient</span>, this is a write and needs an API key
          granted the <span className="api-inline">WRITE</span> capability.
        </p>
        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            Set <span className="api-inline">externalId</span> here so the{' '}
            <Link to="/docs/api/external-references">usage-ingestion API</Link> can find this
            subscription by <span className="api-inline">externalSubscriptionId</span>. Without it,
            a subscription created only through <span className="api-inline">createClient</span> has
            nothing for a usage-reporting integration to resolve against &mdash; every usage call
            for that client fails with <span className="api-inline">404</span> until someone sets it
            by hand on the dashboard.
          </span>
        </div>

        <pre className="api-code">{CREATE_SUBSCRIPTION_MUTATION}</pre>

        <h3 className="api-sub-heading">Input fields</h3>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">clientId</span>
              <span className="api-badge api-badge-cond">one of</span>
            </div>
            <div className="dp-field-v">The client&rsquo;s Revvly id.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">clientExternalId</span>
              <span className="api-badge api-badge-cond">one of</span>
            </div>
            <div className="dp-field-v">
              The client&rsquo;s own externalId, as set on{' '}
              <span className="api-inline">createClient</span> or the dashboard. Supplying both{' '}
              <span className="api-inline">clientId</span> and{' '}
              <span className="api-inline">clientExternalId</span>, or neither, is a{' '}
              <span className="api-inline">BAD_REQUEST</span>. An unresolved client is{' '}
              <span className="api-inline">NOT_FOUND</span>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">externalId</span>
            </div>
            <div className="dp-field-v">
              Your own reference for the subscription (unique per tenant). A duplicate returns{' '}
              <span className="api-inline">CONFLICT</span>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">period</span>
              <span className="api-badge api-badge-req">required</span>
            </div>
            <div className="dp-field-v">
              One of <span className="api-inline">MONTHLY</span>,{' '}
              <span className="api-inline">QUARTERLY</span>,{' '}
              <span className="api-inline">YEARLY</span>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">currency</span>
            </div>
            <div className="dp-field-v">
              Must be one of the client&rsquo;s region&rsquo;s supported currencies; defaults to the
              region&rsquo;s default currency.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">startDate</span>
              <span className="api-badge api-badge-req">required</span>
            </div>
            <div className="dp-field-v">ISO 8601.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">endDate</span>
            </div>
            <div className="dp-field-v">ISO 8601. Optional for an auto-renewing subscription.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">autoRenew</span>
            </div>
            <div className="dp-field-v">Whether the subscription auto-renews. Default true.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">bundleId</span> /{' '}
              <span className="api-inline">moduleId</span> /{' '}
              <span className="api-inline">moduleIds</span> /{' '}
              <span className="api-inline">variableComponentIds</span> /{' '}
              <span className="api-inline">components</span>
              <span className="api-badge api-badge-cond">at least one</span>
            </div>
            <div className="dp-field-v">
              What the subscription bills &mdash; a bundle, one or more modules, standalone Variable
              Components, or components attached directly via{' '}
              <span className="api-inline">components</span> (below). At least one is required; a
              subscription that bills nothing is a <span className="api-inline">BAD_REQUEST</span>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">components</span>
            </div>
            <div className="dp-field-v">
              Variable Components attached directly, each with its own{' '}
              <span className="api-inline">componentId</span>, optional{' '}
              <span className="api-inline">includedQuantity</span> (units included before overage),
              and optional <span className="api-inline">startingQuantity</span> &mdash; the quantity
              provisioned at creation, e.g. a starting seat or child count. That seeds the initial
              usage counter, so the subscription-start invoice bills any overage above the inclusion
              correctly, and usage reported afterward through the{' '}
              <Link to="/docs/api/external-references">usage-ingestion API</Link> is a delta from
              that baseline rather than a raw total.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">trialDays</span>
            </div>
            <div className="dp-field-v">
              A free trial of 1 to 365 days. The subscription starts as{' '}
              <span className="api-inline">TRIAL</span>: usage can be reported (add seats during the
              trial), nothing is invoiced and nothing counts as revenue. When the trial ends it
              converts to <span className="api-inline">ACTIVE</span> on its own, its first period
              starts that day and is invoiced in advance at the quantities reported by then.{' '}
              <span className="api-inline">startDate</span> must not be in the future, and{' '}
              <span className="api-inline">endDate</span> cannot be combined with a trial.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">notes</span>
            </div>
            <div className="dp-field-v">Free-text notes about the subscription.</div>
          </div>
        </div>

        <span className="api-code-label">Response</span>
        <pre className="api-code">{CREATE_SUBSCRIPTION_RESPONSE}</pre>

        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            <strong>Deliberately not exposed here:</strong> historical invoice backfill, billing
            schedules, initial discounts, legacy catalog-version pins, and plan-change timelines
            &mdash; the dashboard subscription wizard&rsquo;s authoring surface for migrating
            existing subscription histories. Those stay dashboard-only; this mutation covers the
            common case of provisioning a new subscription going forward. To change the plan or
            cancel later, see{' '}
            <a href="#existing-subscription">Work with an existing subscription</a>.
          </span>
        </div>
      </section>

      {/* ── Existing subscriptions ─────────────────────────────── */}
      <section className="dp-section">
        <AnchorHeading id="existing-subscription">Work with an existing subscription</AnchorHeading>
        <p className="dp-section-sub">
          Every write takes a subscription by its Revvly id or your externalId and needs{' '}
          <span className="api-inline">WRITE</span>. A subscription outside a scoped key reads as
          not found.
        </p>
        <div className="dp-fields-table api-field-table-mb-sm">
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">subscription(id | externalId)</span>
            </div>
            <div className="dp-field-v">
              One subscription. Its <span className="api-inline">items</span> list what it bills
              for: the plan (bundle, modules or a custom price) with its price per period, and each
              metered component with the quantity it stands at now and how many are included.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">changeSubscriptionQuantity</span>
            </div>
            <div className="dp-field-v">
              Set a metered component&rsquo;s total, for example seats. An increase on a component
              billed in advance is invoiced now for the rest of the period, prorated by day, and the
              invoice comes back; a decrease takes effect without a credit. Nothing is invoiced
              during a free trial. Pass <span className="api-inline">preview: true</span> to see{' '}
              <span className="api-inline">chargedNow</span> before committing. Asking for the
              quantity it already has returns <span className="api-inline">changed: false</span>, so
              a retry is safe.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">changeSubscriptionPlan</span>
            </div>
            <div className="dp-field-v">
              Move a subscription to another bundle or set of modules. Send{' '}
              <span className="api-inline">bundleId</span> and/or{' '}
              <span className="api-inline">moduleIds</span> (the exact set of modules it should
              have), an optional <span className="api-inline">reason</span> kept in its history, and{' '}
              <span className="api-inline">timing</span>:{' '}
              <span className="api-inline">IMMEDIATE</span> invoices or credits the difference for
              the rest of the period now; <span className="api-inline">NEXT_RENEWAL</span> applies
              the change at the next renewal and invoices nothing today. Leave a field out to keep
              it, send <span className="api-inline">bundleId: null</span> to remove the bundle, or
              an empty <span className="api-inline">moduleIds</span> to remove the modules; the
              subscription must keep a bundle or at least one module. A change the plan rules refuse
              comes back as <span className="api-inline">BAD_REQUEST</span> with the reason.
              Recording a past change, pinning an archived catalog version, choosing a tier and
              settling a prepaid span stay in the dashboard.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">cancelSubscription</span>
            </div>
            <div className="dp-field-v">
              It stays in service until its end date and does not renew. Cancelled during a free
              trial, it is never billed.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">setSubscriptionAutoRenew</span>
            </div>
            <div className="dp-field-v">Turn automatic renewal on or off.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">endSubscriptionTrial</span>
            </div>
            <div className="dp-field-v">
              End a free trial now: the first period starts today and is invoiced in advance.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">invoice(id)</span>
            </div>
            <div className="dp-field-v">One invoice, or null.</div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <h2>Save a card on file</h2>
        <p className="dp-section-sub">
          With Stripe connected under Settings &rarr; Payments, invoices are charged to the
          client&rsquo;s card when they are issued. The card is collected on your own page by
          Stripe.js and never passes through Revvly.
        </p>
        <div className="dp-fields-table api-field-table-mb-sm">
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">startPaymentMethodSetup</span>
            </div>
            <div className="dp-field-v">
              Takes a client by <span className="api-inline">clientId</span> or{' '}
              <span className="api-inline">clientExternalId</span> and returns{' '}
              <span className="api-inline">clientSecret</span>,{' '}
              <span className="api-inline">publishableKey</span> and, on a connected account,{' '}
              <span className="api-inline">stripeAccountId</span>. Load Stripe.js with those and
              confirm the setup with the Payment Element; once Stripe confirms it, the card becomes
              the client&rsquo;s default. Needs <span className="api-inline">WRITE</span>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">Client.paymentMethod</span>
            </div>
            <div className="dp-field-v">
              The card on file &mdash; brand, last four digits, expiry &mdash; or null.
            </div>
          </div>
        </div>
      </section>

      {/* ── Errors ─────────────────────────────────────────────── */}
      <section className="dp-section">
        <h2>Errors</h2>
        <p className="dp-section-sub">
          Errors follow the GraphQL convention &mdash; an <span className="api-inline">errors</span>{' '}
          array with a stable code in <span className="api-inline">extensions.code</span> (also
          mirrored as a top-level <span className="api-inline">code</span>). Internal details are
          never leaked in production.
        </p>
        <pre className="api-code">{ERROR}</pre>
        <div className="dp-fields-table">
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">UNAUTHENTICATED</span>
            </div>
            <div className="dp-field-v">Missing or invalid API key.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">FORBIDDEN</span>
            </div>
            <div className="dp-field-v">Key lacks the capability the operation requires.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">BAD_REQUEST</span>
            </div>
            <div className="dp-field-v">
              Invalid arguments (e.g. both or neither of id / externalId).
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">NOT_FOUND</span>
            </div>
            <div className="dp-field-v">No matching resource in your tenant.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">CONFLICT</span>
            </div>
            <div className="dp-field-v">
              A uniqueness constraint was violated &mdash; e.g. creating or renaming a client to a{' '}
              <span className="api-inline">name</span> or{' '}
              <span className="api-inline">externalId</span> another client in your tenant already
              uses.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">RATE_LIMITED</span>
            </div>
            <div className="dp-field-v">
              Per-key rate limit exceeded; see <span className="api-inline">Retry-After</span>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">QUERY_DEPTH_EXCEEDED</span>
            </div>
            <div className="dp-field-v">Query nesting exceeds the allowed depth.</div>
          </div>
        </div>
      </section>

      {/* ── Schema changes ───────────────────────────────────── */}
      <section className="dp-section">
        <h2>How the schema changes</h2>
        <p className="dp-section-sub">
          The schema expands additively (new fields and types, deprecations via{' '}
          <span className="api-inline">@deprecated</span> &mdash; never breaking changes), so what
          you build today keeps working. New fields and mutations are announced in the{' '}
          <Link to="/docs/changelog">changelog</Link>.
        </p>
      </section>

      {/* ── Related ────────────────────────────────────────────── */}
      <section className="dp-section">
        <h2>Related</h2>
        <ul className="dp-connects">
          <li>
            <Link to="/docs/api/events" style={{ color: 'inherit', textDecoration: 'none' }}>
              Event Ingestion API ›
            </Link>
          </li>
          <li>
            <Link
              to="/docs/api/external-references"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              External references &amp; ID discovery ›
            </Link>
          </li>
          <li>
            <Link to="/docs/api/mcp" style={{ color: 'inherit', textDecoration: 'none' }}>
              Connect Claude with MCP ›
            </Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
